import * as Sentry from '@sentry/browser';

import { push } from 'connected-react-router';
import {
  getDeliveryFee,
  orderDisabled,
  recentOrders as recentOrdersMainLayout,
  refreshCart,
  updateItemCart,
} from 'containers/MainLayout/actions';
import { getStore, getStoreWithRetry } from 'containers/MainLayout/saga';
import {
  selectBuyer,
  selectCart,
  selectCountPendingOrder,
  selectOrderDisabled,
  selectPendingApprovalMode,
  selectUser,
} from 'containers/MainLayout/selectors';
import { all, call, fork, put, select, take } from 'redux-saga/effects';
import { calculateVoucher, createOrder, createOrderDraft, getOrder, getVouchers } from 'utils/apollo';
import {
  calculateVoucher as calculateVoucherAction,
  checkout as checkoutActions,
  orderDraft as orderDraftActions,
  setCheckout,
  setSomethingChanged,
  voucher as voucherActions,
} from './actions';

import { message } from 'antd';
import messages from 'containers/CheckoutPage/messages';
import { computeCheckoutQuantityAndTotal } from 'containers/CheckoutStep1Page/saga';
import MainActionTypes from 'containers/MainLayout/constants';
import { CartItem, ItemDiscount, Voucher } from 'types/schema';
import calculateVAT from 'utils/calculateVAT';
import formDateTimeFromGraphQL from 'utils/formatDateTime';
import triggerGA4EcommerceEvent, { EventName } from 'utils/triggerGA4Event';
import ActionTypes, { VOUCHER_STATUS } from './constants';
import {
  selectCheckout,
  selectCurrentAppliedVoucher,
  selectDiscountedValue,
  selectOrderDraftToken,
  selectVouchers,
} from './selectors';

export const isEqualCartItems = (cartItems: CartItem[], newCartItems: CartItem[]) => {
  if (cartItems?.length !== newCartItems?.length) {
    return false;
  }
  const cartItemsMap = new Map();
  const newCartItemsMap = new Map();

  for (let i = 0; i < cartItems.length; i += 1) {
    cartItemsMap.set(cartItems[i]?.product?.id, {
      ...cartItems[i].product,
      quantity: cartItems[i].quantity,
    });
    newCartItemsMap.set(newCartItems[i]?.product?.id, {
      ...newCartItems[i].product,
      quantity: newCartItems[i].quantity,
    });
  }
  const isNumberItemsInCartChange =
    [...new Set([...cartItemsMap.keys(), ...newCartItemsMap.keys()])].length !== cartItems.length;

  if (isNumberItemsInCartChange) {
    return false;
  }
  const hasProductItemChange = [...cartItemsMap.entries()].some(([key, currentProduct]) => {
    const latestProduct = newCartItemsMap.get(key);

    const currentProductPrice = calculateVAT(
      currentProduct.priceV2?.price || 0,
      currentProduct.priceV2?.vat || 0,
      !!currentProduct.priceV2?.vatIncluded,
    ).beforeVat;
    const latestProductPrice = calculateVAT(
      latestProduct.priceV2?.price || 0,
      latestProduct.priceV2?.vat || 0,
      !!latestProduct.priceV2?.vatIncluded,
    ).beforeVat;

    if (currentProductPrice !== latestProductPrice || currentProduct.quantity !== latestProduct.quantity) {
      return true;
    }
    return false;
  });
  if (hasProductItemChange) {
    return false;
  }
  return true;
};

function* createOrderDraftFlow() {
  const store = yield call(getStore);
  if (!store) {
    return;
  }
  yield put(orderDraftActions.request());

  const response = yield call(createOrderDraft, {
    input: {
      storeId: store.id,
    },
  });

  if (!response.errors) {
    yield put(orderDraftActions.success(response));
    yield put(push('/checkout'));
  } else {
    yield put(orderDraftActions.failure(response.errors));
    response.errors.map((e: any) => message.error(e.message));
  }
}
function* handleValidateVoucher(selectedVoucher: Voucher) {
  const store = yield call(getStoreWithRetry);
  const buyer = yield select(selectBuyer());
  const user = yield select(selectUser());
  if (!store) {
    return;
  }
  const draftToken = yield select(selectOrderDraftToken());
  const input = {
    draftOrderId: draftToken,
    storeId: store.id,
    voucherIds: [selectedVoucher.id],
    userId: user.id,
    buyerId: Number(buyer.id),
  };
  const response = yield call(calculateVoucher, input);
  if (!response?.errors && response?.total >= 0) {
    return response;
  }
  return null;
}

function* getVoucher(action) {
  const { payload } = action;
  const store = yield call(getStoreWithRetry);
  const buyer = yield select(selectBuyer());
  const currentCart = yield select(selectCart());
  if (!store) {
    return;
  }
  const draftToken = yield select(selectOrderDraftToken());
  const input = {
    storeId: store?.internalId || 0,
    buyerId: Number(buyer.id),
    storeUuid: store.id,
    draftOrderId: draftToken,
    cartTotal: payload?.total,
    itemUuids: currentCart?.cartSet?.[0]?.items?.map((item) => item?.product?.id) || [],
  };
  try {
    yield put(voucherActions.request());
    const response = yield call(getVouchers, input);
    if (!response?.errors) {
      yield put(voucherActions.success({ data: response?.data }));
    } else {
      yield put(voucherActions.failure(response.errors));
    }
  } catch (error) {
    yield put(voucherActions.failure(new Error(`Unexpected error: ${error}`)));
  }
}

function* updateSelectedVoucher({
  selectedVoucher,
  voucherStatus,
  discountedValue,
  itemDiscount,
}: {
  selectedVoucher: Voucher | null;
  voucherStatus: VOUCHER_STATUS | null;
  discountedValue: number;
  itemDiscount?: ItemDiscount[];
}) {
  yield put({
    type: ActionTypes.SET_VOUCHER,
    payload: {
      currentAppliedVoucher: selectedVoucher,
      status: voucherStatus,
      discountedValue: discountedValue,
      itemDiscount: itemDiscount || [],
      loading: false,
    },
  });

  const cart = yield select(selectCart());

  const checkoutQuantityAndTotalResult = computeCheckoutQuantityAndTotal(cart.cartSet, itemDiscount || []);
  yield put(setCheckout(checkoutQuantityAndTotalResult));
}

function* setVoucher(action) {
  const { payload } = action;
  if (payload?.code && payload?.total) {
    const vouchers = yield select(selectVouchers());
    const currentAppliedVoucher = yield select(selectCurrentAppliedVoucher());

    let selectedVoucher: Voucher | null = null;

    // if vouchers is empty, set voucher to invalid
    if (!vouchers?.length) {
      yield call(updateSelectedVoucher, {
        selectedVoucher: null,
        voucherStatus: VOUCHER_STATUS.INVALID,
        discountedValue: -1,
        itemDiscount: [],
      });
      return;
    }

    if (currentAppliedVoucher && currentAppliedVoucher.code === payload.code) {
      selectedVoucher = currentAppliedVoucher;
    } else {
      selectedVoucher = vouchers?.find((voucher) => voucher?.code === payload?.code);
    }

    // if voucher is not found, set voucher to invalid
    if (!selectedVoucher) {
      yield call(updateSelectedVoucher, {
        selectedVoucher: null,
        voucherStatus: VOUCHER_STATUS.INVALID,
        discountedValue: -1,
        itemDiscount: [],
      });
      return;
    }

    if (!selectedVoucher.isDisabled) {
      yield put(calculateVoucherAction.request());
      const discountedVoucherResponse = yield call(handleValidateVoucher, selectedVoucher);

      // if discounted value is null, set voucher to invalid
      if (!discountedVoucherResponse) {
        yield call(updateSelectedVoucher, {
          selectedVoucher: null,
          voucherStatus: VOUCHER_STATUS.INVALID,
          discountedValue: -1,
        });
        return;
      }

      // if discounted value is less than or equal to 0, or greater than total, set voucher to not satisfied
      if (discountedVoucherResponse?.total <= 0 || discountedVoucherResponse?.total > payload?.total) {
        yield call(updateSelectedVoucher, {
          selectedVoucher: selectedVoucher,
          voucherStatus: VOUCHER_STATUS.NOT_SATISFIED,
          discountedValue: -1,
          itemDiscount: [],
        });
        return;
      }
      yield call(updateSelectedVoucher, {
        selectedVoucher: selectedVoucher,
        voucherStatus: VOUCHER_STATUS.APPLIED,
        discountedValue: discountedVoucherResponse.total,
        itemDiscount: discountedVoucherResponse.itemDiscount,
      });
      return;
    }
    {
      const isExpired = Date.now() > new Date(formDateTimeFromGraphQL(selectedVoucher.endDate)).getTime();
      // if voucher is expired, set voucher to expired
      if (isExpired) {
        yield call(updateSelectedVoucher, {
          selectedVoucher: null,
          voucherStatus: VOUCHER_STATUS.EXPIRED,
          discountedValue: -1,
          itemDiscount: [],
        });
        return;
      }

      yield call(updateSelectedVoucher, {
        selectedVoucher: null,
        voucherStatus: VOUCHER_STATUS.INVALID,
        discountedValue: -1,
        itemDiscount: [],
      });
    }
  }
  // when there is no payload data
  else {
    yield call(updateSelectedVoucher, {
      selectedVoucher: null,
      voucherStatus: null,
      discountedValue: -1,
      itemDiscount: [],
    });
  }
}

function* checkoutFlow() {
  yield put(checkoutActions.request());
  const currentAppliedVoucher = yield select(selectCurrentAppliedVoucher());

  let draftToken = yield select(selectOrderDraftToken());
  const currentCart = yield select(selectCart());

  yield put(refreshCart());
  yield all([take(MainActionTypes.FETCH_CART_SUCCESS), take(MainActionTypes.FETCH_ORDER_DISABLED_SUCCESS)]);

  const orderDisabledResponse = yield select(selectOrderDisabled());

  // if order is disabled, show error message and return
  if (orderDisabledResponse.orderDisabled) {
    message.error(messages.orderDisabled);
    yield put(checkoutActions.failure());
    return;
  }

  const cart = yield select(selectCart());

  // if cart items has been changed, show error message and return
  if (
    cart.notAvailableProducts?.length > 0 ||
    cart.cartSet[0].total < cart?.store?.moq ||
    !isEqualCartItems(cart?.cartSet[0].items, currentCart?.cartSet[0].items)
  ) {
    yield put(checkoutActions.failure());
    yield put(setSomethingChanged());
    return;
  }

  const store = yield call(getStore);
  if (!store) {
    return;
  }
  if (!draftToken) {
    draftToken = yield select(selectOrderDraftToken());
  }

  const totalAfterAppliedVouchers = yield select(selectDiscountedValue());
  const input = {
    draftToken: draftToken,
    storeId: store.id,
    voucherIds: currentAppliedVoucher ? [currentAppliedVoucher.id] : [],
    totalAfterAppliedVouchers: totalAfterAppliedVouchers,
  };

  const response = yield call(createOrder, input);
  if (response.errors) {
    yield put(checkoutActions.failure(response.errors));
    for (const e of response.errors) {
      if (e.message.toLowerCase().includes('voucher') && currentAppliedVoucher) {
        yield call(updateSelectedVoucher, {
          selectedVoucher: currentAppliedVoucher,
          voucherStatus: VOUCHER_STATUS.INVALID,
          discountedValue: -1,
          itemDiscount: [],
        });
      } else {
        message.error(e.message);
      }
    }
  } else {
    yield put(
      checkoutActions.success({
        status: 'CHECKOUT_SUCCESS',
        orders: response.orders,
      }),
    );
    yield put(updateItemCart.success());
    const isPending = yield select(selectPendingApprovalMode());
    if (isPending) {
      const currentCount = yield select(selectCountPendingOrder());
      yield put(recentOrdersMainLayout.success({ countPendingOrders: currentCount + 1 }));
    }
    yield put(
      setCheckout({
        deliveryNotes: [],
        selectedDeliveryNotes: {},
        quantity: {},
        totalOfSupplier: {},
        totalBeforeVATOfSupplier: {},
        timeSlot: {},
        deliveryDate: {},
        total: 0,
      }),
    );
    yield fork(updateSelectedVoucher, {
      selectedVoucher: null,
      voucherStatus: null,
      discountedValue: -1,
      itemDiscount: [],
    });
    window.localStorage.setItem('dayDelivery', '');
    triggerGA4EcommerceEvent(EventName.PURCHASE, {
      ...cart.cartSet[0],
      id: response?.orders[0].id,
    });
  }
}

function* onTrackOrdersCompleted(action) {
  const response = yield all([...action.payload.map((orderId) => call(getOrder, { id: orderId }))]);

  if (response.errors) {
    Sentry.captureException(response.errors);
  }
}

function* getDeliveryFeeFlow() {
  while (true) {
    yield take(ActionTypes.SET_CHECKOUT_TOTAL);
    const checkoutData = yield select(selectCheckout());
    yield put(getDeliveryFee.request({ totalPrice: checkoutData?.total }));
  }
}

export default function* checkoutPageSaga() {
  yield all(
    [
      [ActionTypes.CREATE_ORDER_DRAFT, createOrderDraftFlow],
      [ActionTypes.GET_VOUCHER, getVoucher],
      [ActionTypes.APPLY_VOUCHER, setVoucher],
      [ActionTypes.CHECKOUT_CART, checkoutFlow],
      [ActionTypes.TRACK_ORDERS_COMPLETED, onTrackOrdersCompleted],
    ].map(([actionName, handler]) =>
      fork(function* () {
        while (true) {
          const action = yield take(actionName as any);
          yield call(handler as any, action as any);
        }
      }),
    ),
  );
  yield fork(getDeliveryFeeFlow);
  yield put(orderDisabled.request());
}
